/**
 * AppleWalletButton.jsx - Boton para agregar cartilla de salud a Apple Wallet
 *
 * Props:
 * - objetoId: ID de la mascota (para pases autenticados)
 * - token: Token publico (para pases desde cartilla compartida)
 * - nombreMascota: Nombre para mostrar en el boton
 * - className: Clases CSS adicionales
 */
import React, { useState } from 'react';
import { API_URL } from '../services/api/config';
import { getAuthHeaders } from '../services/api/utils/fetchWrapper';

export default function AppleWalletButton({
  objetoId,
  token,
  nombreMascota,
  className = ''
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    setLoading(true);
    setError(null);

    try {
      let url;
      let fetchOptions = {};

      if (token) {
        // Pase desde cartilla publica (sin autenticacion)
        url = `${API_URL}/api/wallet/health-card-public/${token}`;
      } else if (objetoId) {
        // Pase autenticado
        url = `${API_URL}/api/wallet/health-card/${objetoId}`;
        const headers = await getAuthHeaders();
        fetchOptions = { headers };
      } else {
        throw new Error('Se requiere objetoId o token');
      }

      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al generar el pase');
      }

      // Descargar el archivo .pkpass
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Crear link temporal para descarga
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${(nombreMascota || 'mascota').replace(/[^a-zA-Z0-9]/g, '_')}_cartilla.pkpass`;

      // En iOS, el archivo .pkpass se abre automaticamente en Wallet
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpiar URL blob
      window.URL.revokeObjectURL(blobUrl);

    } catch (err) {
      console.error('[AppleWalletButton] Error:', err);
      setError(err.message || 'Error al descargar el pase');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={className}>
      <button
        onClick={handleDownload}
        disabled={loading}
        className="btn d-flex align-items-center justify-content-center gap-2"
        style={{
          backgroundColor: '#000',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '10px 20px',
          fontSize: '14px',
          fontWeight: 500,
          cursor: loading ? 'wait' : 'pointer',
          opacity: loading ? 0.7 : 1,
          transition: 'opacity 0.2s ease'
        }}
      >
        {loading ? (
          <>
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
            Generando...
          </>
        ) : (
          <>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ marginRight: '4px' }}
            >
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Agregar a Wallet
          </>
        )}
      </button>

      {error && (
        <div
          className="text-danger small mt-2"
          style={{ fontSize: '12px' }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
